import * as React from 'react';
import { Box, Container, Typography, Grid, Link, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import AlertCircleIcon from '@mui/icons-material/ErrorOutline';
import SparklesIcon from '@mui/icons-material/AutoAwesome';

const comparisons = [
    {
        category: 'Root Cause Analysis',
        traditional: 'Manual investigation, 7-14 days per issue',
        ncpro: 'AI-powered analysis in under 5 minutes',
        impact: '95% time reduction'
    },
    {
        category: 'Problem Definition',
        traditional: 'Hours of meetings and documentation',
        ncpro: 'AI-assisted suggestions, clear definitions in minutes',
        impact: '90% faster'
    },
    {
        category: 'Team Expertise Required',
        traditional: 'Only senior engineers can perform proper 8D',
        ncpro: 'Anyone can complete expert-level analysis with AI guidance',
        impact: 'Zero training needed'
    },
    {
        category: 'Report Generation',
        traditional: 'Manual compilation takes 2-6 hours',
        ncpro: 'One-click reports in 30 seconds',
        impact: '99% time saved'
    },
    {
        category: 'Pattern Recognition',
        traditional: 'Rely on memory and manual searches',
        ncpro: 'AI identifies recurring issues automatically',
        impact: '89% fewer repeats'
    },
    {
        category: 'Audit Readiness',
        traditional: 'Scramble for weeks to prepare documentation',
        ncpro: 'Always audit-ready with instant compliance reports',
        impact: 'Always prepared'
    },
    {
        category: 'Corrective Action Suggestions',
        traditional: 'Brainstorming sessions, hit or miss',
        ncpro: 'AI recommendations based on proven solutions',
        impact: '97% success rate'
    },
    {
        category: 'Setup & Implementation',
        traditional: 'Weeks of configuration and training',
        ncpro: 'Fully operational in instant',
        impact: 'Immediate ROI'
    }
];

export default function Comparison() {
    const theme = useTheme();

    const bluePrimary = '#1E5BB8';
    const blueDark = '#0047AB';
    const redColor = theme.palette.error.main;
    const greenColor = theme.palette.success.main;

    return (
        <Box
            component="section"
            sx={{
                py: { xs: 8, md: 12 },
                background: `linear-gradient(to bottom, white, ${theme.palette.grey[50]}, white)`,
                position: 'relative',
                overflow: 'hidden',
            }}
        >

            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 384,
                    height: 384,
                    backgroundColor: '#FEE2E2',
                    borderRadius: '50%',
                    filter: 'blur(48px)',
                    opacity: 0.2,
                    transform: 'translateX(-50%)',
                    zIndex: 0,
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: 384,
                    height: 384,
                    backgroundColor: '#DBEAFE',
                    borderRadius: '50%',
                    filter: 'blur(48px)',
                    opacity: 0.2,
                    transform: 'translateX(50%)',
                    zIndex: 0,
                }}
            />

            <Container maxWidth="xl" sx={{ px: 3, position: 'relative', zIndex: 10 }}>
                <Box sx={{ textAlign: 'center', maxWidth: 680, mx: 'auto', mb: { xs: 8, md: 10 } }}>
                    <Box sx={{ display: 'inline-block', mb: 2 }}>
                        <Typography
                            component="span"
                            sx={{
                                background: `linear-gradient(to right, ${bluePrimary}, ${blueDark})`,
                                color: 'white',
                                px: 3,
                                py: 1,
                                borderRadius: 999,
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                textTransform: 'uppercase',
                                letterSpacing: 1.5,
                                boxShadow: theme.shadows[3],
                            }}
                        >
                            The Clear Choice
                        </Typography>
                    </Box>
                    <Typography
                        component="h2"
                        sx={{
                            fontSize: { xs: '2.5rem', md: '3rem' },
                            fontWeight: 700,
                            color: 'grey.900',
                            mb: 3,
                            lineHeight: 1.1,
                        }}
                    >
                        Traditional Methods vs{' '}
                        <Box component="span" sx={{
                            background: `linear-gradient(to right, ${bluePrimary}, ${blueDark})`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            textFillColor: 'transparent',
                        }}>
                            NC Pro AI
                        </Box>
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: '1.25rem',
                            color: 'grey.600',
                        }}
                    >
                        See why forward-thinking quality managers are making the switch
                    </Typography>
                </Box>

                <Box sx={{ maxWidth: '72rem', mx: 'auto' }}>
                    <Grid container spacing={3} sx={{ mb: 6, alignItems: 'stretch' }}>
                        <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }} />

                        <Grid item xs={12} md={4}>
                            <Box
                                sx={{
                                    backgroundColor: '#FEF2F2',
                                    border: '2px solid #FECACA',
                                    borderRadius: 3,
                                    p: 2,
                                    textAlign: 'center',
                                    height: '100%',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                                    <AlertCircleIcon sx={{ width: 20, height: 20, color: redColor }} />
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#450A0A' }}>
                                        Traditional Methods
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ color: '#B91C1C' }}>
                                    Slow, Manual, Expensive
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Box
                                sx={{
                                    background: `linear-gradient(to bottom right, ${bluePrimary}, ${blueDark})`,
                                    borderRadius: 3,
                                    p: 2,
                                    textAlign: 'center',
                                    boxShadow: theme.shadows[6],
                                    height: '100%',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                                    <SparklesIcon sx={{ width: 20, height: 20, color: '#FDE047' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                                        NC Pro
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ color: '#DBEAFE' }}>
                                    Fast, AI-Powered, Efficient
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        {comparisons.map((item, index) => (
                            <Grid
                                container
                                key={index}
                                spacing={3}
                                alignItems="center"
                                sx={{
                                    backgroundColor: 'white',
                                    borderRadius: 3,
                                    p: 2,
                                    border: '2px solid',
                                    borderColor: theme.palette.grey[100],
                                    transition: 'all 0.3s',
                                    '&:hover': {
                                        boxShadow: theme.shadows[6],
                                    },
                                }}
                            >
                                <Grid item xs={12} md={4}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.grey[900] }}>
                                        {item.category}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Box
                                        sx={{
                                            backgroundColor: '#FEF2F2',
                                            borderRadius: 2,
                                            p: 2,
                                            border: '1px solid #FECACA',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                            <CloseIcon sx={{ width: 20, height: 20, color: redColor, flexShrink: 0, mt: 0.5 }} />
                                            <Typography variant="body2" sx={{ color: theme.palette.grey[700] }}>
                                                {item.traditional}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Box
                                        sx={{
                                            background: 'linear-gradient(to bottom right, #EFF6FF, #EEF2FF)',
                                            borderRadius: 2,
                                            p: 2,
                                            border: `2px solid ${bluePrimary}`,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
                                            <CheckIcon sx={{ width: 20, height: 20, color: greenColor, flexShrink: 0, mt: 0.5 }} />
                                            <Typography variant="body2" sx={{ color: theme.palette.grey[900], fontWeight: 500 }}>
                                                {item.ncpro}
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                backgroundColor: '#D1FAE5',
                                                color: '#065F46',
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: 999,
                                                display: 'inline-block',
                                            }}
                                        >
                                            {item.impact}
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        ))}
                    </Box>

                    <Box
                        sx={{
                            mt: 6,
                            background: `linear-gradient(to right, ${blueDark}, ${bluePrimary})`,
                            borderRadius: 3,
                            p: { xs: 4, md: 5 },
                            color: 'white',
                            boxShadow: theme.shadows[15],
                        }}
                    >
                        <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>The Bottom Line</Typography>
                                <Typography variant="h6" sx={{ color: '#DBEAFE', mb: 4 }}>
                                    Traditional methods cost you time, money, and missed opportunities. NC Pro delivers results from day one.
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                backgroundColor: '#4ADE80',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        ><CheckIcon sx={{ width: 20, height: 20, color: theme.palette.grey[900] }} />
                                        </Box>
                                        <Typography variant="body1" sx={{ fontSize: '1.125rem' }}>87% cost reduction on average</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                backgroundColor: '#4ADE80',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <CheckIcon sx={{ width: 20, height: 20, color: theme.palette.grey[900] }} />
                                        </Box>
                                        <Typography variant="body1" sx={{ fontSize: '1.125rem' }}>95% faster issue resolution</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                backgroundColor: '#4ADE80',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <CheckIcon sx={{ width: 20, height: 20, color: theme.palette.grey[900] }} />
                                        </Box>
                                        <Typography variant="body1" sx={{ fontSize: '1.125rem' }}>ROI in less than 2 weeks</Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box
                                    sx={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        backdropFilter: 'blur(4px)',
                                        borderRadius: 3,
                                        p: 4,
                                        border: '2px solid rgba(255, 255, 255, 0.2)',
                                    }}
                                >
                                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                                        <Typography variant="h2" sx={{ fontWeight: 700, color: '#FDE047', mb: 0.5 }}>10X</Typography>
                                        <Typography variant="subtitle1" sx={{ color: '#DBEAFE' }}>Faster Than Traditional</Typography></Box>
                                    <Link
                                        href="#demo"
                                        underline="none"
                                        sx={{
                                            display: 'block',
                                            width: '100%',
                                            backgroundColor: '#FACC15',
                                            color: theme.palette.grey[900],
                                            px: 4,
                                            py: 2,
                                            borderRadius: 3,
                                            fontWeight: 700,
                                            fontSize: '1.125rem',
                                            textAlign: 'center',
                                            transition: 'all 0.3s',
                                            boxShadow: theme.shadows[6],
                                            '&:hover': {
                                                backgroundColor: '#FCD34D',
                                                transform: 'scale(1.02)',
                                                boxShadow: theme.shadows[10],
                                            },
                                        }}
                                    >Experience the Difference
                                    </Link>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}
